import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { MaskPipe } from 'ngx-mask';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-companies-details-users',
  templateUrl: './dashboard-companies-details-users.component.html',
  styleUrls: ['./dashboard-companies-details-users.component.scss'],
})
export class DashboardCompaniesDetailsUsersComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() companyId: number = 0;
  @Input() activeTab: string = '';
  @Output() userAddedEvent = new EventEmitter();

  private destroy$ = new Subject<void>();

  permissionObj = {
    canAdd: true,
    canFilter: true,
    canChange: true,
    canInactive: true,
  };

  canViewDetails = true;

  data: any[] = [];

  filterForm = this.formBuilder.group({
    name: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    document_number: [null, []],
  });

  departmentForm = this.formBuilder.group({
    department: [null, [Validators.required]],
    roles: [null, [Validators.required]],
  });

  newDepartment = false;
  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  profileOptions: SelectItem[] = [];
  editingId;
  resultsPerPage = 10;
  private dataLoaded = false;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedUser: any = null;
  isActive: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private maskPipe: MaskPipe,
    private companyService: CompanyService
  ) {}

  ngOnInit(): void {
    this.setPermissions();
    this.canViewDetails = this.authService.verifyPermission(
      'de_can_view_company_users_details'
    );

    // Lazy loading: só carrega dados quando componente é inicializado (quando aba está ativa)
    if (!this.dataLoaded) {
      if (!this.companyId || this.companyId === 0) {
        // Aguardar um pouco para o companyId ser passado via @Input
        setTimeout(() => {
          if (this.companyId && this.companyId > 0) {
            this.dataLoaded = true;
            this.getData();
          }
        }, 100);
      } else {
        this.dataLoaded = true;
        this.getData();
      }
    }

    // Listener para quando um usuário for adicionado
    this.companyService.userAdded
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loading = true;
        this.getData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando a aba mudar e não for mais 'users', resetar os filtros
    if (
      changes['activeTab'] &&
      changes['activeTab'].previousValue === 'users' &&
      changes['activeTab'].currentValue !== 'users'
    ) {
      this.resetFiltersOnTabChange();
    }
    // Quando voltar para a aba 'users', recarregar dados sem filtros
    else if (changes['activeTab'] && changes['activeTab'].currentValue === 'users') {
      // Verifica se o componente já foi inicializado
      if (this.dataLoaded) {
        // Resetar filtros e recarregar dados
        this.hasFilters = false;
        this.appliedFilters = [];
        this.filterForm.reset();
        this.getData();
      }
    }
  }

  resetFiltersOnTabChange() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
  }

  async getData(page: number = 1) {
    this.loading = true;

    try {
      const filters: any = {};

      // Construir filtros a partir do formulário (se houver valores)
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            if (key === 'document_number') {
              newValue = value.replace(/[^\d]/g, '');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v2/user_group_user/`,
        token: true,
        params: {
          company: this.companyId,
          page: page,
          ...filters,
          type: 'list',
        },
      });

      // Tratar resposta: pode ter estrutura diferente
      this.resultsPerPage =
        response.results_per_page || response.resultsPerPage || 10;
      this.offset = response.offset || 0;
      this.count = response.count || 0;
      this.data = response.results || [];
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
    }
  }

  handleCancelDepartment() {
    this.newDepartment = false;
    this.editingId = null;
    this.departmentForm.reset();
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Nome',
      document_number: 'CPF / CNPJ',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        // Tratamento especial para CPF/CNPJ (já vem formatado do formulário)
        if (key === 'document_number') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            if (key === 'document_number') {
              newValue = value.replace(/[^\d]/g, '');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v2/user_group_user/`,
        token: true,
        params: {
          company: this.companyId,
          page: 1,
          ...filters,
          type: 'list',
        },
      });

      // Tratar resposta: pode ter estrutura diferente
      this.count = response.count || 0;
      this.data = response.results || [];
      this.offset = response.offset || 0;
      if (response.results_per_page) {
        this.resultsPerPage = response.results_per_page;
      }

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.showFilterModal = false;
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
    }
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_company_users`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  toggleUserStatus(id: number) {
    const findedUser = this.data.find((item) => item.id === id);
    this.selectedUser = findedUser;
    this.isActive = findedUser.status === 'Ativo';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      const res = await this.api.patch({
        route: `api/v2/user_group_user/${this.selectedUser.id}/`,
        token: true,
        body: {
          status: this.selectedUser.status === 'Ativo' ? 'Inativo' : 'Ativo',
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status do usuário.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedUser = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar usuário' : 'Ativar usuário';
  }

  getModalMessage(): string {
    return `Tem certeza de que deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } o usuário "${
      this.selectedUser?.email
    }"? Todas as permissões vinculadas a esse usuário também serão afetadas por essa operação.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} usuário`;
  }

  redirectToDetails(id: any) {
    const companyId = this.route.snapshot.params.id;
    this.router.navigateByUrl(
      `/app/permissions/companies/${companyId}/user/details/${id}`
    );
  }

  formatCpf(value: string) {
    const newValue = this.maskPipe.transform(value, '000.000.000-00');

    return newValue;
  }

  formatGroups(groups: string[]) {
    if (groups.length > 3) {
      const rest = groups.length - 3;
      return `${groups.slice(0, 3).join(', ')} +${rest}`;
    }
    return groups.join(', ');
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    try {
      // Se já vem no formato "DD/MM/YYYY HH:MM"
      if (dateStr.includes('/') && dateStr.includes(' ')) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');

        return {
          date: `${day}/${month}/${year}`,
          time: `às ${hours}:${minutes}`,
        };
      }

      // Se for ISO datetime
      const date = new Date(dateStr);

      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        return { date: '-', time: '' };
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${day}/${month}/${year}`,
        time: `às ${hours}:${minutes}`,
      };
    } catch (error) {
      return { date: '-', time: '' };
    }
  }

  async refreshData() {
    await this.getData();
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
